import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "../ProtectedRoute";
import ItemList from "../components/ItemList/ItemList";
import InsightsPage from "../components/insights/InsightsPage";
import MarketInsights from "../components/insights/Market/MarketInsights";
import HobbyTimeline from "../components/insights/Market/HobbyTimeline";
import Analytics from "../analytics/Analytics";
import HobbliHub from "../components/HobbliHub";
import HobbliSocial from "../components/HobbliSocial";
import SocialMessages from "../components/SocialMessages";
import ArticlesPage from "../components/insights/Guides/ArticlesPage";
import HobbliShop from "../components/marketplace/HobbliShop";
import AboutUs from "../components/AboutUs";
import ContactUs from "../components/ContactUs";
import TermsofUse from "../components/TermsofUse";
import PrivacyPolicy from "../components/PrivacyPolicy";
import FAQ from "../components/FAQ";
import GameRoom from "../components/GameRoom";
import GameRoomBlackjack from "../components/games/blackjack/Blackjack";
import OnlineBlackjack from "../components/games/blackjack/OnlineBlackjack";
import GameRoomChess from "../components/games/chess/GameRoomChess";
import OnlineChess from "../components/games/chess/OnlineChess";
import LoginPage from "../LoginPage";
import HolladaiRoutes from "../holladai/HolladaiRoutes";
import MarketplaceItem from "../components/marketplace/MarketplaceItem";
import MarketplaceCart from "../components/marketplace/MarketplaceCart";
import MarketplaceCheckout from "../components/marketplace/MarketplaceCheckout";
import BuyerOrders from "../components/marketplace/BuyerOrders";
import AdminPage from "../AdminPage";
import SellerDashboard from "../components/seller/SellerDashboard";
import SellerApplicationPage from "../components/seller/SellerApplicationPage";
import PublicShopStorefront from "../components/marketplace/PublicShopStorefront";
import Minesweeper from "../components/games/minesweeper/Minesweeper";
import BlockDrop from "../components/games/BlockDrop/BlockDrop";
import MeteorSmash from "../components/games/meteor-smash/MeteorSmash";
import Pinball from "../components/games/pinball/Pinball";
import Checklist from "../components/insights/Checklist/Checklist";


function SellerRouteGate({ sellerAccess }) {
  const isAdmin = sellerAccess?.isAdmin ?? sellerAccess?.is_admin ?? false;
  const sellerStatus = sellerAccess?.sellerAccountStatus ?? sellerAccess?.seller_account_status ?? null;
  const shopStatus = sellerAccess?.shopStatus ?? sellerAccess?.shop_status ?? null;
  const termsAccepted = sellerAccess?.termsAccepted ?? sellerAccess?.terms_accepted ?? false;
  const approved = sellerStatus === "active" && shopStatus === "active" && termsAccepted === true;

  if (isAdmin || approved) return <SellerDashboard sellerAccess={sellerAccess} />;
  return <Navigate to="/sell" replace />;
}

function AppRoutes({
  token,
  userItemCount,
  cards,
  collections,
  handleDeleteItem,
  refreshCards,
  refreshCollections,
  currentUser,
  sellerAccess,
  canAccessAdminConsole = false,
  authMode,
  setAuthMode,
  handleLoginSupabase,
  handleRegisterSupabase,
  navigate,
  sessionChecked,
  fetchUnreadMessages,
  showHobbliVision,
  setShowHobbliVision,
}) {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/shop" replace />} />

       {/* HolladAI */}
      <Route path="/holladai/*" element={
        <ProtectedRoute token={token}>
          <HolladaiRoutes />
        </ProtectedRoute>
      }/>

      {/* Analytics */}
      <Route path="/analytics" element={<Analytics />} />


        {/* Admin Console */}
      <Route
        path="/admin"
        element={
          canAccessAdminConsole ? (
            <AdminPage currentUser={currentUser} />
          ) : (
            <Navigate to="/shop" replace />
          )
        }
      />

      {/* Hobbli Insights */}
      <Route path="/insights" element={
        <ProtectedRoute token={token}>
          <InsightsPage />
        </ProtectedRoute>
      }/>
      <Route path="/insights/checklists" element={
        <ProtectedRoute token={token}>
          <Checklist user={currentUser} />
        </ProtectedRoute>
      }/>
      <Route path="/insights/market" element={
        <ProtectedRoute token={token}>
          <MarketInsights />
        </ProtectedRoute>
      }/>
      <Route path="/insights/market/timeline" element={
        <ProtectedRoute token={token}>
          <HobbyTimeline />
        </ProtectedRoute>
      }/>

      {/* Preserve old checklist bookmarks while moving discovery into Insights. */}
      <Route path="/checklists" element={<Navigate to="/insights/checklists" replace />} />

      {/* Hub */}
      <Route path="/hub" element={
        <ProtectedRoute token={token}>
          <HobbliHub currentUser={currentUser} />
        </ProtectedRoute>
      }/>

      {/* Hobbli Social */}
      <Route path="/social" element={
        <ProtectedRoute token={token}>
          <HobbliSocial currentUser={currentUser} />
        </ProtectedRoute>
      }/>

      {/* Social Messages */}
      <Route path="/messages" element={
        <ProtectedRoute token={token}>
          <SocialMessages
            currentUser={currentUser}
            onBack={() => navigate("/social")}
            refreshUnread={() => fetchUnreadMessages(currentUser.id)}
          />
        </ProtectedRoute>
      }/>

      <Route path="/collection" element={
        <ProtectedRoute token={token}>
          <div className="dashboard">
            <span className="stats">
              <strong>{userItemCount}</strong> items in your collections.
            </span>
          </div>
          <div className="main-content">
            <ItemList
              cards={cards}
              collections={collections}
              currentUser={currentUser}
              onDeleteItem={handleDeleteItem}
              onCardsChanged={refreshCards}
              onCollectionsChanged={refreshCollections}
              showHobbliVision={showHobbliVision}
              setShowHobbliVision={setShowHobbliVision}
            />
          </div>
        </ProtectedRoute>
      }/>


      {/* Seller application is available to signed-in Hobbli users. */}
      <Route
        path="/sell"
        element={
          <ProtectedRoute token={token}>
            <SellerApplicationPage sellerAccess={sellerAccess} />
          </ProtectedRoute>
        }
      />

      {/* Preserve the original seller-start bookmark while keeping one Seller Dashboard. */}
      <Route path="/seller/start" element={<Navigate to="/seller" replace />} />

      <Route
        path="/seller"
        element={
          <ProtectedRoute token={token}>
            <SellerRouteGate sellerAccess={sellerAccess} />
          </ProtectedRoute>
        }
      />

      {/* Public Pages */}
      <Route path="/articles" element={<ArticlesPage />} />
      <Route path="/hobblishop" element={<Navigate to="/shop" replace />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/about/contact" element={<ContactUs />} />
      <Route path="/about/terms" element={<TermsofUse />} />
       <Route path="/about/privacy" element={<PrivacyPolicy />} />
      <Route path="/about/faq" element={<FAQ />} />
      {/* Preserve existing bookmarks while consolidating About & Support. */}
      <Route path="/contact" element={<Navigate to="/about/contact" replace />} />
      <Route path="/terms" element={<Navigate to="/about/terms" replace />} />
       <Route path="/privacy" element={<Navigate to="/about/privacy" replace />} />
      <Route path="/faq" element={<Navigate to="/about/faq" replace />} />
      <Route path="/games" element={<GameRoom currentUser={currentUser} />} />
      <Route path="/games/blackjack" element={<GameRoomBlackjack currentUser={currentUser} />} />
      <Route path="/games/blackjack/online" element={<OnlineBlackjack currentUser={currentUser} />} />
      <Route path="/games/chess" element={<GameRoomChess currentUser={currentUser} />} />
      <Route path="/games/chess/online" element={<OnlineChess currentUser={currentUser} />} />
      <Route path="/games/minesweeper" element={<Minesweeper currentUser={currentUser} />} />
      <Route path="/games/block-drop" element={<BlockDrop currentUser={currentUser} />} />
      <Route path="/games/meteor-smash" element={<MeteorSmash currentUser={currentUser} />} />
      <Route path="/games/pinball" element={<Pinball currentUser={currentUser} />} />
      <Route path="/marketplace/:listingId" element={<MarketplaceItem />} />
      <Route path="/cart" element={<MarketplaceCart />} />
      <Route path="/checkout" element={<MarketplaceCheckout />} />
      <Route path="/orders" element={
        <ProtectedRoute token={token}>
          <BuyerOrders />
        </ProtectedRoute>
      } />
      <Route 
        path="/shop" 
        element={
          <HobbliShop 
            currentUser={currentUser} 
            sessionChecked={sessionChecked} 
          />} 
      />
      <Route path="/login" element={
        <LoginPage
          mode={authMode}
          onLogin={handleLoginSupabase}
          onRegister={handleRegisterSupabase}
          onSwitchToRegister={() => setAuthMode('register')}
          onBackToLogin={() => setAuthMode('login')}
        />
      }/>

      {/* Public seller storefronts. Keep this after all explicit root routes. */}
      <Route path="/:shopSlug" element={<PublicShopStorefront />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/shop" replace />} />
    </Routes>
  );
}

export default AppRoutes;
